import React from "react";
import { connect } from "react-redux";

import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import { Alignment, Side, Size } from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ButtonMenu from "../core/components/buttons/ButtonMenu";
import Button from "../core/components/buttons/Button";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import Collapsible from "../core/components/collapsible/Collapsible";
import ContextFragment from "../core/components/contextmenu/ContextFragment";
import ContextMenuItem from "../core/components/contextmenu/ContextMenuItem";
import QueuedExportFile from "../core/types/system/QueuedExportFile";
import FileTypes from "../core/types/enums/FileTypes";
import QueuedExportSubTypes from "../core/types/enums/QueuedExportSubType";
import { addExports } from "../core/state/administration/Exports";
import User from "../core/types/core/User";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import { openRecycled, RecycledType } from "../core/state/system/Recycled";
import Recycled from "../core/components/recycled/Recycled";
import { Timetable } from "../core/types/archive/Timetable";
import {
    deleteTimetables,
    initialiseTimetableDetails,
} from "../core/state/archive/Timetables";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";

import "./PublishedTimetables.scss";

interface PublishedTimetablesProps {
    user: User;
    timetables: Timetable[];

    setPageLoading: (loading: boolean) => void;
    initialiseTimetableDetails: () => Promise<void>;
    deleteTimetables: (timetables: Timetable[]) => Promise<void>;
    addExports: (exports: QueuedExportFile[]) => Promise<void>;
    openRecycled: (types: RecycledType[]) => void;
}

interface PublishedTimetablesState {
    groupsOpen: Map<string, boolean>;
    timetables: {
        groupName: string;
        open: boolean;
        timetables: Timetable[];
    }[];
    selections: Timetable[];
}

class PublishedTimetables extends React.Component<
    PublishedTimetablesProps,
    PublishedTimetablesState
> {
    state: PublishedTimetablesState = {
        groupsOpen: new Map(),
        timetables: [],
        selections: [],
    };

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await this.props.initialiseTimetableDetails();
            this.buildState();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    componentDidUpdate(
        prevProps: PublishedTimetablesProps,
        prevState: PublishedTimetablesState,
    ) {
        if (
            prevProps.timetables !== this.props.timetables ||
            prevState.groupsOpen !== this.state.groupsOpen
        )
            this.buildState();
    }

    buildState() {
        this.setState(prevState => ({
            groupsOpen: prevState.groupsOpen,
            timetables: Array.from(
                this.props.timetables.groupBy(item => item.groupName),
            ).map(group => ({
                groupName: group[0],
                open: prevState.groupsOpen.get(group[0]) ?? true,
                timetables: group[1],
            })),
            selections: prevState.selections,
        }));
    }

    toggleOpen(groupName: string, open: boolean) {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            newGroupsOpen.set(groupName, open);
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    selectAll() {
        this.setState({
            selections: this.props.timetables,
        });
    }
    deselectAll() {
        this.setState({
            selections: [],
        });
    }

    expandAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.timetables) {
                newGroupsOpen.set(group.groupName, true);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    collapseAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.timetables) {
                newGroupsOpen.set(group.groupName, false);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    async delete(file?: Timetable) {
        const timetables = file ? [file] : this.state.selections;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to delete these timetables?<br />" +
                `<ul class="indented chevron"><li>${timetables
                    .map(item => item.name)
                    .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await this.props.deleteTimetables(timetables);
                this.setState({
                    selections: [],
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async download(file?: Timetable) {
        const timetables = file ? [file] : this.state.selections;

        const exportItems = timetables.map(
            item =>
                ({
                    fileId: item.fileId,
                    type: FileTypes.TDF, // Type for TDF
                    subType: QueuedExportSubTypes.Undefined, // Type for Undefined
                } as QueuedExportFile),
        );

        try {
            this.props.setPageLoading(true);
            await this.props.addExports(exportItems);

            addAlert({
                type: AlertTypes.Alert,
                text:
                    "Your export file is being created, this may take " +
                    "up to 10 minutes for large files.  We will email " +
                    `${this.props.user.username} when it's ready`,
            });
        } catch (error) {
            addAlert({
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                text:
                    "An error occured while trying to export these files, " +
                    "please try again later. If the problem persists, " +
                    "please contact us for support.",
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    recycleBin() {
        this.props.openRecycled([RecycledType.Archived]);
    }

    render() {
        return (
            <>
                <section data-contextkey="timetables-menu">
                    <MainToolbarContents>
                        <ButtonMenu
                            icon="description"
                            text="Files"
                            tooltip="File management options">
                            <Button
                                icon="checklist_rtl"
                                text="Select All"
                                onClick={() => this.selectAll()}
                            />
                            <Button
                                icon="event_list"
                                text="Deselect All"
                                onClick={() => this.deselectAll()}
                            />
                            <Button
                                icon="add"
                                text="Expand All"
                                onClick={() => this.expandAll()}
                            />
                            <Button
                                icon="remove"
                                text="Collapse All"
                                onClick={() => this.collapseAll()}
                            />
                            <Button
                                icon="delete"
                                text="Delete Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.delete()}
                            />
                            <Button
                                icon="file_download"
                                text="Download Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.download()}
                            />
                        </ButtonMenu>
                        <ToolbarButton
                            icon="delete"
                            text="Recycle Bin"
                            onClick={() => this.recycleBin()}
                        />
                    </MainToolbarContents>

                    {this.props.timetables?.length > 0 ? (
                        <GeneralContainer
                            scaling={true}
                            mid={true}
                            max={true}
                            bufferSide={Side.All}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            alignment={Alignment.Center}>
                            {this.state.timetables.map((group, index) => (
                                <Collapsible
                                    key={index}
                                    open={group.open}
                                    name={group.groupName}
                                    clearanceSide={Side.Bottom}
                                    onOpenChange={open =>
                                        this.toggleOpen(group.groupName, open)
                                    }>
                                    <List<Timetable>
                                        joined={true}
                                        multiSelect={true}
                                        selections={this.state.selections}
                                        selectionChanged={(_, selections) =>
                                            this.setState({ selections })
                                        }>
                                        {group.timetables.map((file, index) => (
                                            <ListItem<Timetable>
                                                key={index}
                                                data={file}
                                                panel={true}
                                                autoHeight={true}
                                                selectable={true}
                                                bufferSide={Side.All}
                                                contextkey="timetables-file"
                                                contextvalue={String(file.id)}>
                                                <div className="twothirds flex-column">
                                                    <div className="twothirds">
                                                        <h3 className="truncated">
                                                            {file.name}
                                                        </h3>
                                                    </div>

                                                    <div className="top-buffer-small">
                                                        <b>Start Date:&nbsp;</b>
                                                        {file.startDate.toLocale()}
                                                    </div>
                                                </div>

                                                {file.status !== "Normal" ? (
                                                    <div
                                                        className={`onethird text-right print-left ${
                                                            file.status ===
                                                            "Error"
                                                                ? "error"
                                                                : ""
                                                        }`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: file.message,
                                                        }}></div>
                                                ) : (
                                                    <div className="onethird text-right print-left">
                                                        <b>Published:&nbsp;</b>
                                                        {file.publishedOn.toLocaleShort()}
                                                    </div>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapsible>
                            ))}
                        </GeneralContainer>
                    ) : (
                        <Text clearanceSide={Side.All}>No published timetables found.</Text>
                    )}
                </section>

                <Recycled />

                <ContextFragment name="timetables-menu">
                    <ContextMenuItem
                        id="timetables_spacer_0"
                        separator={true}
                        contextKey="timetables-file"
                        order={0}
                    />
                    <ContextMenuItem
                        id="timetables_explorer_delete"
                        icon="delete"
                        name="Delete"
                        contextKey="timetables-file"
                        handler={(e, c) =>
                            this.delete(
                                this.props.timetables.find(
                                    item => String(item.id) === c.getValue(),
                                ),
                            )
                        }
                        order={4}
                    />
                    <ContextMenuItem
                        id="timetables_explorer_download"
                        icon="download"
                        name="Download"
                        contextKey="timetables-file"
                        handler={(e, c) =>
                            this.download(
                                this.props.timetables.find(
                                    item => String(item.id) === c.getValue(),
                                ),
                            )
                        }
                        order={5}
                    />
                    <ContextMenuItem
                        id="timetables_spacer_1"
                        separator={true}
                        contextKey="timetables-menu"
                        order={0}
                    />
                    <ContextMenuItem
                        id="timetables_expand_all"
                        icon="add"
                        name="Expand All"
                        contextKey="timetables-menu"
                        handler={this.expandAll.bind(this)}
                        order={1}
                    />
                    <ContextMenuItem
                        id="timetables_collapse_all"
                        icon="remove"
                        name="Collapse All"
                        contextKey="timetables-menu"
                        handler={this.collapseAll.bind(this)}
                        order={2}
                    />
                </ContextFragment>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { timetable } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        timetables: timetable.timetables,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseTimetableDetails: () => initialiseTimetableDetails(),
        deleteTimetables: (timetables: Timetable[]) =>
            unwrappedDispatch(deleteTimetables(timetables)),
        addExports: (exports: QueuedExportFile[]) =>
            unwrappedDispatch(addExports(exports)),
        openRecycled: (types: RecycledType[]) => openRecycled(types),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(PublishedTimetables);
